/**
 * Validate a rule set and return errors (block save) and warnings (informational).
 * @param {{ name: string, defaultAction: string, rules: Array }} ruleSet
 * @returns {{ errors: string[], warnings: string[] }}
 */
export function validateRuleSet({ name, defaultAction, rules }) {
    const errors = []
    const warnings = []
    const referencedRuleSetIds = new Set()

    if (!name || !name.trim()) {
        errors.push("Rule set name is required.")
    }

    if (defaultAction === "exclude" && (!rules || rules.length === 0)) {
        warnings.push('Default action is "Exclude All" with no rules defined. This will exclude all tasks.')
    }

    for (let i = 0; i < (rules || []).length; i++) {
        const rule = rules[i]
        const label = rule.name ? `"${rule.name}"` : `Rule ${i + 1}`

        if (rule.type === "ruleSetReference") {
            if (!rule.ruleSetId) {
                errors.push(`Rule set reference ${i + 1} must choose a rule set.`)
            } else if (referencedRuleSetIds.has(rule.ruleSetId)) {
                // FIQ-1634: the same rule set must not be referenced more than once
                const refLabel = rule.ruleSetName ? `"${rule.ruleSetName}"` : "a rule set"
                errors.push(`Rule set reference ${i + 1} duplicates ${refLabel}. Remove the duplicate reference.`)
            } else {
                referencedRuleSetIds.add(rule.ruleSetId)
            }
            continue
        }

        if (!rule.conditions || rule.conditions.length === 0) {
            warnings.push(`${label} has no conditions and will never match.`)
            continue
        }

        // Warn if cost modifier multiplier is unusually high
        if (rule.action?.type === "modifier" && rule.action.multiplier > 5) {
            warnings.push(`${label} has a cost modifier greater than 500%. Are you sure this is correct?`)
        }
        if (rule.action?.type === "timeModifier") {
            if ((Number(rule.action.timeValue) || 0) === 0) {
                warnings.push(`${label} has a time modifier that does not change task duration.`)
            }
            if (rule.action.timeKind === "minutes" && Number(rule.action.timeValue) < 0) {
                warnings.push(`${label} shortens task duration. Check this is intentional.`)
            }
            if (rule.action.timeKind === "percentage" && Math.abs(Number(rule.action.timeValue) || 0) > 5) {
                warnings.push(`${label} has an extreme percentage time modifier.`)
            }
        }
        if (rule.action?.type === "frequencyModifier") {
            const frequencyMultiplier = Number(rule.action.frequencyMultiplier)
            if (!Number.isFinite(frequencyMultiplier) || frequencyMultiplier <= 0) {
                errors.push(`${label} must use a frequency multiplier greater than 0.`)
            } else if (frequencyMultiplier >= 5) {
                warnings.push(`${label} runs matched tasks at least 5x as often. Check this is intentional.`)
            } else if (frequencyMultiplier === 1) {
                warnings.push(`${label} has a frequency modifier that does not change task frequency.`)
            }
        }

        // Check for contradictory classification conditions. This only applies when
        // conditions are AND-combined — under OR, non-overlapping value sets are fine
        // because any one matching satisfies the rule.
        const combinator = rule.combinator === "or" ? "or" : "and"
        if (combinator === "and") {
            const classConditions = (rule.conditions || []).filter(isClassificationCondition)
            if (classConditions.length > 1) {
                const isAnyOf = classConditions.filter(isAnyOfOperator)
                if (isAnyOf.length > 1) {
                    const intersection = isAnyOf.reduce(
                        (acc, c) => {
                            const vals = Array.isArray(c.value) ? c.value : [c.value]
                            return acc.filter((v) => vals.includes(v))
                        },
                        Array.isArray(isAnyOf[0].value) ? [...isAnyOf[0].value] : [isAnyOf[0].value]
                    )

                    if (intersection.length === 0) {
                        warnings.push(`${label} has contradictory classification conditions that can never match.`)
                    }
                }
            }
        }
    }

    return { errors, warnings }
}

function isClassificationCondition(c) {
    return c.field === "classification"
}

function isAnyOfOperator(c) {
    return c.operator === "is any of"
}
